





































import Vue, { VueConstructor } from 'vue';
import {
	mapActions, mapGetters, mapMutations, mapState,
} from 'vuex';
import { debounce } from '@/utilities/helpers';

import { FormatAddress } from '../utilities/formatters';
import NpMultiselect from './NpMultiselect.vue';

interface LocationSelect {
	customerLocations: Locations.Location[];
	recurringLocations: Locations.Location[];
	reservationLocations: Locations.Location[];
	hasLocation: boolean;
	location: Locations.Location;
	locations: Locations.Location[];
	subLocations: Locations.Location[];
	setLocation: (location: string) => void;
	corporateLocations: Locations.Location[]
}

export default (Vue as VueConstructor<Vue & LocationSelect>).extend({
	components: { NpMultiselect },

	props: {
		availableLocations: {} as () => Locations.Location[],
		customer: {
			type: Boolean,
			default: false,
		},
		id: {
			type: String,
			default: 'location-selector',
		},
		locked: {
			type: Boolean,
			default: false,
		},
		type: {
			type: String,
			default: 'all',
		},
	},

	data() {
		return {
			value_change: false,
			reservation_count: 0,
			showPlaceholder: false,
			locationLimit: 100 as number,
			windowWidth: window.innerWidth - 32 as number,
		};
	},

	computed: {
		...mapState('location', ['locations']),
		...mapGetters('location', ['customerLocations', 'hasLocation', 'location', 'recurringLocations', 'reservationLocations', 'subLocations', 'corporateLocations']),

		minWidth(): string {
			let minWidth = 280;

			if (this.windowWidth < minWidth) {
				minWidth = this.windowWidth;
			}

			return `${minWidth}px`;
		},

		settings(): Locations.Settings {
			return this.$store.getters['location/settings'];
		},


		model: {
			get(): Locations.Location {
				switch (this.type) {
					case 'customer':
						return this.customerLocations.find((location: any) => (
							(+location.code === +this.location.code || location.code === this.location.code)
						&& location.codeID === this.location.codeID
						))
							|| ({} as Locations.Location);
					case 'recurring':
						return this.recurringLocations.find((location: any) => (
							(+location.code === +this.location.code || location.code === this.location.code)
						&& location.codeID === this.location.codeID))
							|| ({} as Locations.Location);
					case 'reservation':
						if (this.showPlaceholder && !this.value_change) {
							return { details: { name: 'Select a Location' } } as Locations.Location;
						}
						return this.reservationLocations.find((location: any) => (
							(+location.code === +this.location.code || location.code === this.location.code)
							&& location.codeID === this.location.codeID))
								|| ({} as Locations.Location);
					case 'all':
					default:
						return this.location;
				}
			},
			set(value: Locations.Location) {
				this.showPlaceholder = false;
				this.$nextTick(() => {
					this.value_change = true;
				});
				if (value && value.codeID && value.codeID !== this.location.codeID) {
					this.$store.dispatch('googleEvents/locationChange', {
						newLocationCodeID: `${value.code}-${value.subLocation}`,
					});
					this.setLocation(value.codeID);
					this.$emit('updated', value.codeID, value);
				}
			},
		},

		selectableLocations(): Locations.Location[] {
			if (Array.isArray(this.availableLocations) && this.availableLocations.length) {
				return this.availableLocations;
			}

			switch (this.type) {
				case 'customer':
					return this.customerLocations;
				case 'sub':
					return this.subLocations;
				case 'recurring':
					return this.recurringLocations;
				case 'reservation':
					if (this.showPlaceholder && !this.value_change) {
						return [{ details: { name: 'Select a Location' } } as Locations.Location, ...this.reservationLocations];
					}
					return this.reservationLocations;
				case 'corporate':
					return this.corporateLocations;
				case 'all':
				default:
					return this.locations;
			}
		},
	},

	mounted() {
		const urlParams = new URLSearchParams(window.location.search);
		const hasLocParam = urlParams.has('loc') || urlParams.has('location');

		if (this.type === 'reservation' && this.settings.force_select_location === 1 && !hasLocParam && sessionStorage.getItem('count') !== '1') {
			this.showPlaceholder = true;
			sessionStorage.setItem('count', '1');
			this.reservation_count = 1;
		}

		if (this.selectableLocations.length === 1 && this.subLocations.length === 1) {
			[this.model] = this.selectableLocations;
		}

		if (this.model && this.model.codeID && this.model.codeID !== this.location.codeID) {
			this.setLocation(this.model.codeID as string);
		}

		const firstWithCode = this.selectableLocations.find((loc) => loc.codeID);
		if (firstWithCode && !this.selectableLocations.find((location) => +location.subLocation === +this.location.subLocation && location.code === this.location.code)) {
			this.setLocation(firstWithCode.codeID as string);
		}

		window.onresize = debounce(() => {
			this.$nextTick(() => {
				this.windowWidth = window.innerWidth - 32;
			});
		}, 500, { leading: false, trailing: true });

		if (this.settings.display_all_quotes_from_locations) {
			this.setLocations(this.selectableLocations);
		}
	},

	methods: {
		...mapActions('location', ['setLocation', 'setLocations']),
		...mapMutations('location', ['setLocations']),

		locationAddress(location: Locations.Location): string {
			return FormatAddress({
				address: location.address,
				city: location.city,
				state: location.state,
				zip: location.zip,
				country: location.country,
			}, ' ');
		},
	},
});
